/* Places poll activity labels in the flattened message list. */

import { isAtFirstPosition, isPollMessage, LOCAL_CREATED_PREFIX, POLL_EVENT, toMillis } from './pollRules';

export const POLL_ACTIVITY_ITEM = 'pollActivity';

/**
 * Inserts each label above the first message at or after its anchor time; a poll still at
 * its first position gets its "created" label from the message itself.
 *
 * @param {object[]} items Flattened list items: 'single' and 'grouped' carry messages.
 * @param {object} labelsById Labels of the chat, keyed by activity id.
 * @param {object} pollsById Server polls, keyed by poll id.
 * @returns {object[]} The list with label items added.
 */
export const withPollLabels = (items, labelsById = {}, pollsById = {}) => {
    const labels = withoutDuplicateCreated(Object.values(labelsById)).sort(
        (a, b) => a.anchorTime - b.anchorTime || a.time - b.time,
    );
    const createdPollIds = new Set(
        labels.filter(label => label.event === POLL_EVENT.CREATED).map(label => label.pollId),
    );
    const firstMessage = items.map(messageOf).find(Boolean);
    if (!firstMessage) return items;

    // Labels anchored above the loaded range belong to a page not shown yet.
    const firstTime = toMillis(firstMessage.timestamp);
    let next = labels.findIndex(label => label.anchorTime >= firstTime);
    if (next < 0) next = labels.length;

    const result = [];
    items.forEach(item => {
        const message = messageOf(item);
        if (message) {
            const time = toMillis(message.timestamp);
            const above = [];
            while (next < labels.length && labels[next].anchorTime <= time) above.push(labels[next++]);
            const created = derivedCreatedLabel(message, pollsById, createdPollIds);
            if (created) above.unshift(created);
            above.sort((a, b) => a.time - b.time);
            above.forEach(label =>
                result.push({ type: POLL_ACTIVITY_ITEM, id: `poll-activity-${label.activityId}`, label }),
            );
        }
        result.push(item);
    });
    return result;
};

/**
 * "Created" label of a poll message, built on the client because no event carries it.
 *
 * @param {object} message Poll message.
 * @param {object} poll Server poll, when loaded.
 * @returns {object} Label without time; the caller places it.
 */
export const buildCreatedLabel = (message, poll) => ({
    activityId: `${LOCAL_CREATED_PREFIX}${message.msgId}`,
    pollId: poll?.pollId || message.msgBody?.poll?.pollId,
    messageId: message.msgId,
    event: POLL_EVENT.CREATED,
    actor: poll?.createdBy || message.msgBody?.poll?.createdBy || message.publisherId,
    reason: '',
});

// The first move rebuilds "created" locally; once history brings the stored one, it wins.
const withoutDuplicateCreated = labels => {
    const storedCreated = new Set(
        labels.filter(label => label.event === POLL_EVENT.CREATED && !isLocalCreated(label)).map(label => label.pollId),
    );
    return labels.filter(label => !(isLocalCreated(label) && storedCreated.has(label.pollId)));
};

const isLocalCreated = label => String(label.activityId).startsWith(LOCAL_CREATED_PREFIX);

const messageOf = item => {
    if (item?.type === 'single') return item.message;
    if (item?.type === 'grouped') return item.messages?.[0];
    return null;
};

const derivedCreatedLabel = (message, pollsById, createdPollIds) => {
    if (!isPollMessage(message)) return null;
    const pollId = message.msgBody?.poll?.pollId;
    const poll = pollId ? pollsById[pollId] : null;
    if ((pollId && createdPollIds.has(pollId)) || !isAtFirstPosition(message, poll)) return null;
    // Sorts above every other label of the same message.
    return { ...buildCreatedLabel(message, poll), time: Number.NEGATIVE_INFINITY };
};
